/**
 * Шаг «Заполнение данных» мастера заказа обучения (training-listener-completeness; ТЗ 26.10.2026,
 * п.1.1, п.1.6; ответы заказчицы 27.10, В12, В14, В15): по каждому слушателю — СНИЛС, дата рождения,
 * пол, образование, e-mail, согласие на обработку ПД и, если у него есть программа «ВО/СПО», —
 * документ о высшем или среднем профессиональном образовании. Каждое поле сохраняется в карточку
 * сотрудника сразу (без отдельного «Сохранить»). У слушателя — светофор с подписью: зелёный
 * «Заполнено», жёлтый «Частично», красный «Не заполнено»; места без названного слушателя — красные,
 * с выбором сотрудника. Слушатели — группами по подразделениям.
 *
 * Ответ заказчицы 04.11 к п.1.3: «Заполнено», «Частично», «Не заполнено» вверху — фильтры со
 * счётчиками; слушатели без подразделения — первой группой, у ФИО красный «!» с подсказкой «Укажите
 * подразделение» (ведёт в карточку сотрудника).
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { CircleAlert, Download, SearchX, Users } from 'lucide-react'
import {
  companyApi,
  trainingApi,
  type ListenerStatus,
  type TrainingSelectedEmployee,
  type UpdateEmployeeInput,
} from '@/lib/api'
import {
  EDUCATION_DOCUMENT_LABEL,
  LISTENER_FIELD_LABELS,
  LISTENER_STATUS_VIEW,
  TRAINING_EDUCATION_OPTIONS,
  isTrainingEducationSelected,
  listenerMissing,
  listenerStatus,
} from '@/lib/training'
import { isValidEmail, isValidSnils } from '@/lib/validation'
import { fieldError, getErrorMessage } from '@/lib/errors'
import { downloadFromUrl } from '@/lib/download'
import { groupByDepartment } from '@/lib/structure'
import { UNSPECIFIED_DEPARTMENT, isUnspecifiedDepartment } from '@/lib/department'
import { cn } from '@/lib/utils'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { SnilsInput } from '@/design-system/components/primitives/snils-input'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/design-system/components/primitives/tooltip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { ConsentCell, EducationDocumentCell } from './employee-documents'
import type { UnnamedPlace } from './training-request'


/** id строки слушателя — для перехода «Осталось заполнить» к первой незаполненной. */
export function listenerRowId(personId: string): string {
  return `listener-${personId}`
}

/** Цвет точки светофора (декоративная: подпись рядом — текстом). */
const DOT_CLASS: Record<'green' | 'amber' | 'red', string> = {
  green: 'bg-green',
  amber: 'bg-amber',
  red: 'bg-red',
}

/** Точка и подпись светофора (не только цвет). */
function StatusMark({ dot, label }: { dot: 'green' | 'amber' | 'red'; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className={`inline-block size-dot shrink-0 rounded-round ${DOT_CLASS[dot]}`} />
      {label}
    </span>
  )
}

/** Светофор слушателя: точка и подпись. */
export function ListenerStatusBadge({ employee }: { employee: TrainingSelectedEmployee }) {
  const view = LISTENER_STATUS_VIEW[listenerStatus(employee)]
  return (
    <span className="text-13 text-txt-secondary">
      <StatusMark dot={view.dot} label={view.label} />
    </span>
  )
}

/** Фильтр слушателей по заполненности: все или один статус светофора. */
export type ListenerFilter = 'all' | ListenerStatus

/** Подсказка у слушателя без подразделения (ответ заказчицы 04.11 к п.1.3). */
export const DEPARTMENT_MISSING_HINT = 'Укажите подразделение'

/**
 * Фильтры светофора со счётчиками (ответ заказчицы 04.11 к п.1.3: «Заполнено», «Частично», «Не
 * заполнено» вверху сделать фильтрами) — кнопки-переключатели; «Все» — без фильтра.
 */
function StatusFilters({
  value,
  counts,
  total,
  onChange,
}: {
  value: ListenerFilter
  counts: Record<ListenerStatus, number>
  total: number
  onChange: (next: ListenerFilter) => void
}) {
  const chip = (key: ListenerFilter, label: ReactNode, count: number) => (
    <Button
      key={key}
      type="button"
      size="sm"
      variant="secondary"
      aria-pressed={value === key}
      className={cn(value === key && 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg')}
      onClick={() => onChange(key)}
    >
      {label}
      <span className="tabular-nums text-txt-secondary">{count}</span>
    </Button>
  )
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Фильтр по заполненности">
      {chip('all', 'Все', total)}
      {(Object.keys(LISTENER_STATUS_VIEW) as ListenerStatus[]).map((k) =>
        chip(k, <StatusMark dot={LISTENER_STATUS_VIEW[k].dot} label={LISTENER_STATUS_VIEW[k].label} />, counts[k]),
      )}
    </div>
  )
}

/**
 * Значение поля, которое сохраняется по уходу с него: сохранённое значение карточки подхватывается,
 * только если поле не правили после прошлого сохранённого — ответ на сохранение не затирает то, что
 * человек уже набирает заново.
 */
function useDraft(saved: string): [string, (value: string) => void] {
  const [value, setValue] = useState(saved)
  const previous = useRef(saved)
  useEffect(() => {
    const before = previous.current
    previous.current = saved
    setValue((current) => (current === before ? saved : current))
  }, [saved])
  return [value, setValue]
}

/** Поле с подписью над значением. */
function FieldBlock({
  label,
  children,
  error,
  errorId,
}: {
  label: string
  children: ReactNode
  error?: string
  /** id подсказки об ошибке — поле ссылается на него через `aria-describedby`. */
  errorId?: string
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-12 text-txt-secondary">{label}</span>
      {children}
      {error ? (
        <span id={errorId} className="text-12 text-red">
          {error}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Карточка слушателя: заголовок со светофором, поля — сеткой. Каждое поле сохраняется в карточку
 * сотрудника сразу: СНИЛС и e-mail — по уходу с поля (если значение корректно), дата рождения —
 * когда введена целиком (недописанная по уходу с поля возвращается к сохранённой), пол и
 * образование — выбором. Сохранения карточки идут по очереди, в мастер уходят только сохранённые
 * поля. Некорректное несохранённое значение СНИЛС или e-mail светофор считает незаполненным, и мастер
 * о нём знает — с шага не уйти, пока его не исправят или не очистят.
 */
export function ListenerCard({
  employee,
  departmentMissing,
  disabled,
  onSaving,
  onPatch,
  onInvalidDraft,
}: {
  employee: TrainingSelectedEmployee
  /** Подразделение не указано — красный «!» у ФИО со ссылкой в карточку сотрудника. */
  departmentMissing: boolean
  disabled: boolean
  onSaving: (delta: number) => void
  onPatch: (personId: string, patch: Partial<TrainingSelectedEmployee>) => void
  onInvalidDraft: (personId: string, invalid: boolean) => void
}) {
  const personId = employee.personId
  const [snils, setSnils] = useDraft(employee.snils ?? '')
  const [email, setEmail] = useDraft(employee.email ?? '')
  const [error, setError] = useState<string | null>(null)
  /** Очередь сохранений карточки: следующее поле уходит после ответа на предыдущее. */
  const queue = useRef<Promise<void>>(Promise.resolve())

  function save(field: keyof UpdateEmployeeInput & keyof TrainingSelectedEmployee, value: string) {
    setError(null)
    onSaving(1)
    queue.current = queue.current.then(async () => {
      try {
        await companyApi.updateEmployee(personId, { [field]: value } as UpdateEmployeeInput)
        onPatch(personId, { [field]: value })
      } catch (err) {
        setError(
          fieldError(err, field) ??
            getErrorMessage(err, 'Не удалось сохранить — проверьте данные и попробуйте снова'),
        )
      } finally {
        onSaving(-1)
      }
    })
  }

  const snilsInvalid = snils.trim() !== '' && !isValidSnils(snils)
  const emailInvalid = email.trim() !== '' && !isValidEmail(email.trim())
  const hasInvalidDraft = snilsInvalid || emailInvalid
  useEffect(() => {
    if (!hasInvalidDraft) return
    onInvalidDraft(personId, true)
    return () => onInvalidDraft(personId, false)
  }, [personId, hasInvalidDraft, onInvalidDraft])
  // Светофор — по тому, что видно в полях: некорректный несохранённый СНИЛС или e-mail не
  // «заполнен», даже если в карточке сотрудника лежит прежнее корректное значение.
  const shown = {
    ...employee,
    snils: snilsInvalid ? '' : employee.snils,
    email: emailInvalid ? '' : employee.email,
  }
  const missing = listenerMissing(shown)
  const snilsErrorId = `${listenerRowId(personId)}-snils-error`
  const emailErrorId = `${listenerRowId(personId)}-email-error`
  const education = isTrainingEducationSelected(employee.education) ? (employee.education ?? '') : ''
  const name = employee.fullName

  return (
    <li id={listenerRowId(employee.personId)} className="flex scroll-mt-24 flex-col gap-3 p-3 nw:px-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 break-words font-medium text-txt">
            {name}
            {departmentMissing ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Подразделение указывают в карточке сотрудника — она откроется в новой вкладке,
                      заполненные здесь данные не потеряются. */}
                  <a
                    href={`/staff/${employee.personId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${DEPARTMENT_MISSING_HINT} — откроется карточка сотрудника`}
                    className="ds-focus-ring inline-flex shrink-0 rounded-full text-red"
                  >
                    <CircleAlert className="size-4 stroke-current" aria-hidden />
                  </a>
                </TooltipTrigger>
                <TooltipContent>{DEPARTMENT_MISSING_HINT}</TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          {employee.position ? <div className="text-13 text-txt-secondary">{employee.position}</div> : null}
        </div>
        <ListenerStatusBadge employee={shown} />
      </div>
      {/* Четыре колонки (ответ заказчицы 04.11 к п.1.3): плашки уже, все поля — в две строки, в том
          числе документ о ВО/СПО. */}
      <div className="grid grid-cols-1 gap-3 nw:grid-cols-4">
        <FieldBlock
          label="СНИЛС"
          error={snilsInvalid ? 'Проверьте СНИЛС: 11 цифр и контрольная сумма' : undefined}
          errorId={snilsErrorId}
        >
          <SnilsInput
            value={snils}
            invalid={snilsInvalid}
            disabled={disabled}
            aria-label={`СНИЛС — ${name}`}
            aria-describedby={snilsInvalid ? snilsErrorId : undefined}
            onChange={setSnils}
            onBlur={() => {
              if (snils !== (employee.snils ?? '') && (snils.trim() === '' || isValidSnils(snils))) {
                save('snils', snils)
              }
            }}
          />
        </FieldBlock>
        <FieldBlock label="Дата рождения">
          <DateInput
            value={employee.birthDate ?? ''}
            disabled={disabled}
            aria-label={`Дата рождения — ${name}`}
            restoreOnIncompleteBlur
            onChange={(v) => {
              if (v !== (employee.birthDate ?? '')) save('birthDate', v)
            }}
          />
        </FieldBlock>
        <FieldBlock label="Пол">
          <Select
            value={employee.gender === 'm' || employee.gender === 'f' ? employee.gender : ''}
            disabled={disabled}
            onValueChange={(v) => save('gender', v)}
          >
            <SelectTrigger aria-label={`Пол — ${name}`}>
              <SelectValue placeholder="Пол" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="m">Мужской</SelectItem>
              <SelectItem value="f">Женский</SelectItem>
            </SelectContent>
          </Select>
        </FieldBlock>
        <FieldBlock label="Образование">
          <Select
            value={education}
            disabled={disabled}
            onValueChange={(v) => save('education', v)}
          >
            <SelectTrigger aria-label={`Образование — ${name}`}>
              <span className="min-w-0 flex-1 truncate text-left">
                <SelectValue placeholder="Образование" />
              </span>
            </SelectTrigger>
            <SelectContent>
              {TRAINING_EDUCATION_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldBlock>
        <FieldBlock label="E-mail" error={emailInvalid ? 'Проверьте адрес почты' : undefined} errorId={emailErrorId}>
          <Input
            type="email"
            value={email}
            invalid={emailInvalid}
            disabled={disabled}
            aria-label={`E-mail — ${name}`}
            aria-describedby={emailInvalid ? emailErrorId : undefined}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => {
              const v = email.trim()
              if (v !== (employee.email ?? '') && (v === '' || isValidEmail(v))) {
                save('email', v)
              }
            }}
          />
        </FieldBlock>
        <FieldBlock label="Согласие на обработку ПД">
          <ConsentCell
            employee={employee}
            readOnly={disabled}
            onChanged={(e) =>
              onPatch(personId, {
                hasConsent: e.hasConsent,
                consentFileName: e.consentFileName,
                consentUrl: e.consentUrl,
              })
            }
          />
        </FieldBlock>
        {employee.needsEducationDocument ? (
          <FieldBlock label={EDUCATION_DOCUMENT_LABEL}>
            <EducationDocumentCell
              employee={employee}
              readOnly={disabled}
              onChanged={(e) => onPatch(personId, { educationDocument: e.educationDocument })}
            />
          </FieldBlock>
        ) : null}
      </div>
      {missing.length > 0 ? (
        // Красным — чтобы незаполненное было заметно (ответ заказчицы 04.11 к п.1.3).
        <p className="text-12 text-red">
          Осталось: {missing.map((f) => LISTENER_FIELD_LABELS[f]).join(', ')}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-12 text-red">
          {error}
        </p>
      ) : null}
    </li>
  )
}

export function TrainingStepData({
  employees,
  unnamed,
  disabled,
  onSaving,
  onEmployeePatch,
  onInvalidDraft,
  onPickPlace,
  placesLocked = false,
  individual = false,
}: {
  employees: TrainingSelectedEmployee[]
  /** Позиции, где названы не все слушатели (training-listeners-count). */
  unnamed: (UnnamedPlace & { key: string })[]
  disabled: boolean
  onSaving: (delta: number) => void
  /** Сохранённые поля слушателя — слить в мастер. */
  onEmployeePatch: (personId: string, patch: Partial<TrainingSelectedEmployee>) => void
  /** В карточке слушателя появилось или исчезло некорректное несохранённое значение. */
  onInvalidDraft: (personId: string, invalid: boolean) => void
  /** «Выбрать сотрудников» на место без названного слушателя. */
  onPickPlace: (key: string) => void
  /**
   * Изменения состава на согласовании (training-change-approval): слушателей на места не назвать,
   * данные слушателей правятся как обычно.
   */
  placesLocked?: boolean
  /** Физлицо (individual-training-order): данные и согласие — его собственные. */
  individual?: boolean
}) {
  const [filter, setFilter] = useState<ListenerFilter>('all')
  // Состав отфильтрованного списка фиксируется на момент выбора фильтра: слушатель, которого
  // дозаполнили в «Частично», не пропадает из-под рук посреди ввода; счётчики — живые.
  const [filteredIds, setFilteredIds] = useState<Set<string> | null>(null)
  const counts = useMemo(() => {
    const out: Record<ListenerStatus, number> = { complete: 0, partial: 0, empty: 0 }
    for (const e of employees) out[listenerStatus(e)]++
    return out
  }, [employees])
  function changeFilter(next: ListenerFilter) {
    setFilter(next)
    setFilteredIds(
      next === 'all'
        ? null
        : new Set(employees.filter((e) => listenerStatus(e) === next).map((e) => e.personId)),
    )
  }
  const visible = filteredIds ? employees.filter((e) => filteredIds.has(e.personId)) : employees
  // Без подразделения — первой группой: подразделение нужно указать (ответ заказчицы 04.11).
  const groups = groupByDepartment(
    visible,
    (e) => (isUnspecifiedDepartment(e.departmentName) ? '' : e.departmentName),
    UNSPECIFIED_DEPARTMENT,
    true,
  )

  if (employees.length === 0 && unnamed.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="Слушатели не выбраны"
        description="Вернитесь к шагу «Программы и сотрудники» и добавьте программы"
      />
    )
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-13 text-txt-secondary">
          {individual
            ? 'Данные сохраняются сразу. Подтвердить данные можно, когда всё заполнено. Согласие на обработку персональных данных: скачайте шаблон, подпишите и загрузите скан или фото.'
            : 'Данные сохраняются в карточки сотрудников сразу. Подтвердить данные можно, когда все слушатели заполнены. Согласие на обработку персональных данных: скачайте шаблон, подпишите с сотрудником и загрузите скан или фото к его фамилии.'}
        </p>
        {/* Согласие — условие подтверждения данных (ответ заказчицы 27.10, В17): шаблон нужен здесь,
            а не только после отправки. */}
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
          onClick={() => downloadFromUrl(trainingApi.consentTemplateUrl())}
        >
          Скачать шаблон согласия
        </Button>
      </div>
      {employees.length > 0 ? (
        <StatusFilters value={filter} counts={counts} total={employees.length} onChange={changeFilter} />
      ) : null}

      {unnamed.length > 0 ? (
        <section aria-label="Не указаны слушатели" className="rounded-10 border border-alert-danger bg-red-bg p-3 nw:px-4">
          <h3 className="mb-2 font-semibold text-txt">
            <StatusMark dot="red" label="Не указаны слушатели" />
          </h3>
          <ul className="flex flex-col gap-2">
            {unnamed.map((u) => (
              <li key={u.key} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-txt">
                  {u.label}: не указано {u.places - u.named} из {u.places}
                </span>
                <Button size="sm" variant="secondary" disabled={disabled || placesLocked} onClick={() => onPickPlace(u.key)}>
                  Выбрать сотрудников
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {filter !== 'all' && visible.length === 0 ? (
        <EmptyState
          icon={SearchX}
          title="Нет слушателей с таким статусом"
          className="py-6"
          action={
            <Button size="sm" variant="secondary" onClick={() => changeFilter('all')}>
              Показать всех
            </Button>
          }
        />
      ) : null}

      {/* Слушатели без подразделения — в общей с «Не указано» группе (training-listeners-count),
          первой. */}
      {groups.map((group) => (
        <Fragment key={group.department}>
          <section aria-label={`Слушатели — ${group.department}`}>
            <h3 className="mb-2 text-13 font-semibold uppercase tracking-wide text-txt-secondary">
              {group.department}
            </h3>
            <ul className="divide-y divide-line rounded-10 border border-line bg-panel">
              {group.items.map((e) => (
                <ListenerCard
                  key={e.personId}
                  employee={e}
                  departmentMissing={!individual && isUnspecifiedDepartment(e.departmentName)}
                  disabled={disabled}
                  onSaving={onSaving}
                  onPatch={onEmployeePatch}
                  onInvalidDraft={onInvalidDraft}
                />
              ))}
            </ul>
          </section>
        </Fragment>
      ))}
    </div>
  )
}
